/**
 * Mastery Module — Public API
 *
 * Single entry point for all mastery-related functionality.
 *
 * Architecture (11 sub-modules):
 * - familyMasteryEngine: ELO-based mastery scoring per family
 * - masteryCanonical: Canonical mastery queries (module, tier, family)
 * - masteryDecay: Time-based forgetting curve (Ebbinghaus, pure)
 * - adaptiveEngine: Question selection orchestrator (v11.2)
 * - selection/*: Pluggable selector modules (config, scoring, tier window,
 *     prerequisites, question selector)
 * - placementEngine: Date-based B/M/E phase + starting-tier resolution
 * - questionAttemptHistory: Per-question attempt log (NOT mastery)
 * - subSkillMastery: Sub-skill level mastery aggregation for UI
 * - skillAggregation: Skill-level mastery aggregation for UI
 * - tierProgress: Tier progression info for UI
 * - smartSessions: Djay's dagplan / SmartSessionCard segments
 * - growthStages: Visual growth-stage labels for mastery scores
 *
 * RC pass (mei 2026): placement-test API (`getPlacementKey`,
 * `PlacementResult`) removed — placement tests were retired in v11.
 */

// ── Family Mastery Engine ────────────────────────────────────────────────────
export {
  updateFamilyMastery,
  getFamilyMasteryKey,
  parseMasteryKey,
  isGroupScopedKey,
} from './familyMasteryEngine'
export type { FamilyAttempt, FamilyMasteryState } from './familyMasteryEngine'

// ── Inferred Mastery (FG3 — cold-start soft-pass via placement) ─────────────
export {
  getInferredMastery,
  isInferredPassing,
  resolveMasterySignal,
  buildPlacementInferredEntry,
  DEFAULT_INFERRED_CONFIDENCE,
  MIN_CONFIDENCE_FOR_PASS,
} from './inferredMastery'
export type {
  InferredMasteryEntry,
  InferredMasterySource,
  ResolvedMastery,
} from './inferredMastery'

// ── Mastery Canonical (Single Source of Truth) ───────────────────────────────
export {
  getFamilyMasteryScore,
  isFamilyMastered,
  getFamilyMasteryLevel,
  getMasteryLevel,
  getMasteryColor,
  getMasteryBgColor,
  getModuleMasteryFromFamilies,
  isModuleMasteredByFamilies,
  getTierMasteryFromFamilies,
  getAllTierMasteryFromFamilies,
  getStrugglingFamiliesForModule,
  getMasteredFamiliesForModule,
  getWeakestFamiliesForModule,
  getWeakestFamiliesGlobal,
  getStrongestFamiliesGlobal,
  getWeakestFamilyEntries,
  getStrongestFamilyEntries,
  countFamiliesByMasteryLevel,
  getFamilyMasteryAttempts,
  hasFamilyAttempts,
  getModuleCoverage,
} from './masteryCanonical'
export type { FamilyMasteryEntry } from './masteryCanonical'

// ── Mastery Decay ────────────────────────────────────────────────────────────
export {
  computeDecayedMastery,
  hasActiveDecay,
  getDecayStatus,
  getDecayPercentage,
  computeAllDecayedMastery,
  MASTERY_DECAY_CONFIG,
} from './masteryDecay'
export type { MasteryDecayConfig } from './masteryDecay'

// ── Adaptive Engine ──────────────────────────────────────────────────────────
export {
  getAdaptiveSessionV11,
  generateSelectionDebugReport,
} from './adaptiveEngine'
export type { SelectionDebugReport } from './adaptiveEngine'

// ── Tier Progress ────────────────────────────────────────────────────────────
export { computeTierProgress } from './tierProgress'
export type { TierProgressInfo } from './tierProgress'

// ── Sub-Skill Mastery ────────────────────────────────────────────────────────
export { computeSubSkillMastery } from './subSkillMastery'

// ── Placement Engine (date-based B/M/E phase) ────────────────────────────────
export {
  resolvePhaseByDate,
  getAutoStartingTier,
  getAutoPhaseLabel,
} from './placementEngine'
export type { SchoolYearPhase } from './placementEngine'

// ── Question Attempt History ─────────────────────────────────────────────────
export {
  updateQuestionState,
  getQuestionStateKey,
  getAnsweredQuestionsForGroup,
  getRecentQuestionsForGroup,
  getReviewQueueForGroup,
  getReviewNeededCountForGroup,
  getQuestionsByStatus,
  getModuleStats,
  evictStaleAttempts,
  getAttemptHistorySize,
} from './questionAttemptHistory'
export type { EvictionReport } from './questionAttemptHistory'

// ── Skill Aggregation ────────────────────────────────────────────────────────
export {
  computeSkillMasteries,
  getActiveSkillCount,
  getSkillToSubSkillMap,
} from './skillAggregation'
export type { SkillMastery } from './skillAggregation'

// ── Growth Stages (mastery visualization) ────────────────────────────────────
export { getGrowthStage } from './growthStages'
export type { GrowthStage } from './growthStages'

// ── Selection Config (public constants) ───────────────────────────────────────
export { V11_CONFIG } from './selection/selectionConfig'

// ── Smart Sessions (HomePage / SmartSessionCard) ─────────────────────────────
export { getSmartSessions } from './smartSessions'
export type {
  SmartSessions,
  SmartSessionKind,
  SmartSessionSegment,
} from './smartSessions'

// ── Domain Types (knowledge state) ───────────────────────────────────────────
export type {
  QuestionStatus,
  QuestionState,
  QuestionAttempt,
  SubSkillProgress,
} from './types'
